import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import SubHeaders from '@/components/Headers/SubHeaders'
import { Button, Checkbox, FormItem, Typogaphy } from 'vnetwork-ui'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import type { IPermissionProps } from '@/types/shared'
import { createRole, getResourcesScopes } from '../../services'
import type { RoleCreatePayload, ResourceScope } from '../../types'
import { createRoleSchema } from './schema'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const Create: React.FC<IPermissionProps> = () => {
    const navigate = useNavigate()

    const rolesRefetch = useDataGridRefetch({
        reactQueryKey: 'roles',
    })

    const { data: resourcesData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.RESOURCES_SCOPES],
        queryFn: getResourcesScopes,
    })

    const { mutateAsync: create, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: createRole,
            },
            {
                reactQueryKeys: QUERY_KEYS.ROLES,
                successMessage: 'Tạo vai trò thành công',
                errorMessage: 'Có lỗi xảy ra khi tạo vai trò',
                onSuccess: () => {
                    rolesRefetch.refetchCurrent()
                    navigate('/roles')
                },
            }
        )

    const { control, handleSubmit, watch, setValue } =
        useForm<RoleCreatePayload>({
            defaultValues: {
                role: '',
                resources: {},
            },
            resolver: yupResolver(createRoleSchema),
        })

    const watchedResources = watch('resources')

    const handleResourceChange = (
        resourceName: string,
        scopeName: string,
        checked: boolean
    ) => {
        const currentScopes = watchedResources[resourceName] || []
        let newScopes: string[]

        if (checked) {
            newScopes = [...currentScopes, scopeName]
        } else {
            newScopes = currentScopes.filter((scope) => scope !== scopeName)
        }

        const newResources = { ...watchedResources }
        if (newScopes.length > 0) {
            newResources[resourceName] = newScopes
        } else {
            delete newResources[resourceName]
        }

        setValue('resources', newResources)
    }

    const handleSelectAllToggle = (resource: ResourceScope) => {
        const currentScopes = watchedResources[resource.name] || []
        const allScopeNames = resource.scopes?.map((scope) => scope.name) || []
        const isAllSelected =
            allScopeNames.length > 0 &&
            allScopeNames.every((scopeName) =>
                currentScopes.includes(scopeName)
            )

        const newResources = { ...watchedResources }

        if (isAllSelected) {
            // Bỏ chọn tất cả
            delete newResources[resource.name]
        } else {
            // Chọn tất cả
            newResources[resource.name] = allScopeNames
        }

        setValue('resources', newResources)
    }

    const onSubmit = async (values: RoleCreatePayload) => {
        try {
            await create({
                ...values,
                role: values.role.trim().toLowerCase(),
            })
        } catch (error) {
            console.error('Error creating role:', error)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const resources =
        resourcesData && 'results' in resourcesData ? resourcesData.results : []

    return (
        <>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: '/',
                        title: 'Trang chủ',
                    },
                    {
                        path: '/roles',
                        title: 'Quản lý vai trò',
                    },
                    {
                        path: '/roles/new',
                        title: 'Tạo mới',
                    },
                ]}
            />

            <div className="bg-white rounded-lg p-4 mt-2">
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-6"
                    data-tour="role-form"
                >
                    <div data-tour="role-name">
                        <FormItem
                            control={control}
                            name="role"
                            type="textfield"
                            label="Tên vai trò *"
                            placeholder="Nhập tên vai trò"
                            size="small"
                        />
                    </div>

                    <div data-tour="role-permissions">
                        <Typogaphy className="text-lg font-semibold text-gray-900 mb-4">
                            Phân quyền *
                        </Typogaphy>

                        <div className="space-y-4">
                            {resources?.map((resource: ResourceScope) => {
                                const currentScopes =
                                    watchedResources[resource.name] || []
                                const allScopeNames =
                                    resource.scopes?.map(
                                        (scope) => scope.name
                                    ) || []
                                const isAllSelected =
                                    allScopeNames.length > 0 &&
                                    allScopeNames.every((scopeName) =>
                                        currentScopes.includes(scopeName)
                                    )

                                return (
                                    <div
                                        key={resource.id}
                                        className="bg-gray-50 rounded-lg p-4"
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <Typogaphy className="font-medium text-gray-800">
                                                {resource.displayName ||
                                                    resource.name}
                                            </Typogaphy>
                                            <Button
                                                type="button"
                                                variant={
                                                    isAllSelected
                                                        ? 'contained'
                                                        : 'outlined'
                                                }
                                                size="small"
                                                onClick={() =>
                                                    handleSelectAllToggle(
                                                        resource
                                                    )
                                                }
                                                className="text-xs"
                                            >
                                                {isAllSelected
                                                    ? 'Bỏ chọn tất cả'
                                                    : 'Chọn tất cả'}
                                            </Button>
                                        </div>

                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                            {resource?.scopes?.map((scope) => {
                                                const isChecked =
                                                    currentScopes.includes(
                                                        scope.name
                                                    )

                                                return (
                                                    <label
                                                        key={scope.id}
                                                        className="flex items-center space-x-2 cursor-pointer"
                                                    >
                                                        <Checkbox
                                                            checked={isChecked}
                                                            onChange={(
                                                                checked
                                                            ) =>
                                                                handleResourceChange(
                                                                    resource.name,
                                                                    scope.name,
                                                                    checked
                                                                )
                                                            }
                                                        />
                                                        <Typogaphy className="text-sm text-gray-700 select-none">
                                                            {scope.name}
                                                        </Typogaphy>
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <div
                        className="flex justify-end space-x-3"
                        data-tour="role-submit"
                    >
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={() => navigate('/roles')}
                        >
                            Hủy
                        </Button>
                        <Button type="submit" disabled={isCreating}>
                            Tạo vai trò
                        </Button>
                    </div>
                </form>
            </div>
        </>
    )
}

export default Create
